import React, { useState, useEffect, useCallback } from 'react';
import { Mail, X, Check } from 'lucide-react';

const SUPPRESS_KEY = 'glow_intake_exit_seen';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Lightweight exit-intent popup that fires only on the intake questions step.
 * Captures email so the user can finish later via inbox + tags as a higher-intent lead.
 *
 * Triggers:
 *   - Mouse leaves toward the top of the viewport (desktop)
 *   - Page hidden via tab switch / mobile background (mobile fallback)
 * Suppressed once per session (sessionStorage).
 */
const IntakeExitPopup = ({ active, currentSection, totalSections }) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const close = useCallback(() => setOpen(false), []);

  // Mark as shown so we don't pester the user again this session
  const markShown = () => {
    try { sessionStorage.setItem(SUPPRESS_KEY, '1'); } catch { /* noop */ }
  };

  useEffect(() => {
    if (!active) return undefined;
    if (typeof window === 'undefined') return undefined;
    if (sessionStorage.getItem(SUPPRESS_KEY)) return undefined;

    const handleMouseLeave = (e) => {
      if (e.clientY < 8 && !open) {
        setOpen(true);
        markShown();
      }
    };
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden' && !open) {
        setOpen(true);
        markShown();
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [active, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes('@')) {
      setError('Please enter a valid email');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API}/leads/capture`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          source: 'intake_abandoned',
          progress: progress ?? 0,
          pageUrl: typeof window !== 'undefined' ? window.location.href : '',
        }),
      });
      if (!res.ok) throw new Error('save failed');
      setDone(true);
      // Auto-close after a short success state
      setTimeout(() => setOpen(false), 2200);
    } catch {
      setError("Couldn't save right now. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const progress = totalSections
    ? Math.round((currentSection / totalSections) * 100)
    : null;

  return (
    <div
      className="intake-exit-overlay"
      onClick={close}
      data-testid="intake-exit-popup"
      role="dialog"
      aria-modal="true"
    >
      <div className="intake-exit-modal" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="intake-exit-close"
          onClick={close}
          aria-label="Close"
          data-testid="intake-exit-close"
        >
          <X size={18} />
        </button>

        {done ? (
          <div className="intake-exit-success">
            <div className="intake-exit-success-icon">
              <Check size={28} strokeWidth={2} />
            </div>
            <h3 className="intake-exit-title">Sent. Check your inbox.</h3>
            <p className="intake-exit-sub">
              We saved your spot. You can finish whenever you have a quiet moment.
            </p>
          </div>
        ) : (
          <>
            <div className="intake-exit-icon">
              <Mail size={26} strokeWidth={1.5} />
            </div>
            <span className="intake-exit-eyebrow">Don't lose your progress</span>
            <h3 className="intake-exit-title">
              Want to finish this from your inbox?
            </h3>
            <p className="intake-exit-sub">
              {progress !== null
                ? `You're already ${progress}% through. Drop your email and we'll send you a link so you can finish whenever you have a quiet moment, no judgment.`
                : "Drop your email and we'll send you a link so you can finish whenever you have a quiet moment."}
            </p>

            <form onSubmit={handleSubmit} className="intake-exit-form">
              <input
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => { setEmail(e.target.value); if (error) setError(''); }}
                className="intake-exit-input"
                data-testid="intake-exit-email"
                autoFocus
              />
              {error && <div className="intake-exit-error">{error}</div>}
              <button
                type="submit"
                disabled={submitting}
                className="intake-exit-submit"
                data-testid="intake-exit-submit"
              >
                {submitting ? 'Saving...' : 'Send me a link'}
              </button>
            </form>

            <p className="intake-exit-fineprint">
              We'll only email about your assessment. No spam, ever.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default IntakeExitPopup;
